import React, { useState } from "react";
import Label from "@/components/form/Label";
import { TextField } from "@/components/form/fields";
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';

interface PasswordFieldProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  label: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean | string;
  hint?: string;
  className?: string;
  showToggle?: boolean;
  // Allow external control of password visibility (for shared state scenarios)
  showPassword?: boolean;
  onToggleVisibility?: () => void;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  label,
  placeholder = "Enter password",
  required = false,
  error = false,
  hint,
  className = "",
  showToggle = true,
  showPassword: externalShowPassword,
  onToggleVisibility: externalOnToggleVisibility,
}) => {
  // Use external state if provided, otherwise use internal state
  const [internalShowPassword, setInternalShowPassword] = useState(false);
  const showPassword = externalShowPassword !== undefined ? externalShowPassword : internalShowPassword;

  const togglePasswordVisibility = () => {
    if (externalOnToggleVisibility) {
      externalOnToggleVisibility();
    } else {
      setInternalShowPassword(!internalShowPassword);
    }
  };

  return (
    <div className={`${className || "w-full"} overflow-visible`}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <div className="relative overflow-visible">
        <TextField
          id={id}
          name={name}
          type={showPassword ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          error={!!error}
          hint={typeof error === 'string' ? error : hint}
          required={required}
          className="focus:ring-brand-500/20 focus:border-brand-300 dark:focus:border-brand-800"
          inputClassName="pr-10"
        />
        {showToggle && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-3 top-2.5 z-30 cursor-pointer w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            <GoogleMaterialIcon 
              name={showPassword ? "visibility_off" : "visibility"} 
              size="sm"
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default PasswordField; 